import { creatorbase } from '@round/api';
import { ButtonShell, getTableMetaHelper } from '@round/ui-kit';
import { CellContext } from '@tanstack/react-table';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import styles from './PostsTableAccountCell.module.css';
import UserBadge, { UserBadgeProps } from 'Modules/Plans/User/UserBadge/UserBadge';
import { showNotification } from 'helpers';
import { assets } from '@round/whitelabel-ui';

const { Diagram: DiagramIcon } = assets;

type Row = creatorbase.Post;
export type AccountData = {
    accountId?: number | undefined | null;
    image: string | undefined;
    title: string;
    followerCount?: number | null;
    profileUrl?: string | null;
};

type Context<T extends Row> = CellContext<T, AccountData>;

const getTableMeta = getTableMetaHelper<BasePostTableMeta<Row>>();

const PostsTableAccountCell = <T extends Row>({ row, table, getValue }: Context<T>) => {
    const { isLoading, getIsAccountDataLoading, setAccountModalUserId, onOpenClientAccountModal, managedBy } =
        getTableMeta(table);
    const { accountId, image, title, followerCount, profileUrl } = getValue();

    const userBadgeProps: UserBadgeProps = {
        isLoading: isLoading || getIsAccountDataLoading(row.original),
        image: image,
        title: title,
        followerCount: followerCount,
        profileUrl: profileUrl,
    };

    const handleOpenAccountModal = async () => {
        if (!setAccountModalUserId && !onOpenClientAccountModal) {
            // This means we don't expect to have data for either modal, e.g. YouTube posts, so we can quietly return
            return;
        }

        if (!accountId) {
            showNotification("Couldn't open the user modal", 'error');
            return;
        }

        if (!row.original.client_influencer_account_id) {
            showNotification('No related influencer account found. Opening the demographics modal', 'info');
            setAccountModalUserId?.(accountId);
            return;
        }

        onOpenClientAccountModal?.({
            clientInfluencerAccountId: row.original.client_influencer_account_id,
            platformUserId: accountId,
        });
    };

    return (
        <div className={styles.container}>
            <UserBadge {...userBadgeProps} />

            {(setAccountModalUserId || onOpenClientAccountModal) &&
                // We don't expect client influencer accounts or phyllo data for microwave influencers
                managedBy !== 'microwave' && (
                    <ButtonShell className={styles.modalButton} onClick={handleOpenAccountModal}>
                        <DiagramIcon />
                    </ButtonShell>
                )}
        </div>
    );
};

export default PostsTableAccountCell;
